import React, { useEffect, useState, lazy, createElement, Component } from 'react';
import { CheckSquareIcon, AwardIcon, ShareIcon, BookmarkIcon, RefreshCwIcon, DownloadIcon, TrophyIcon, HeartIcon, BrainIcon, CameraIcon, BadgeIcon, StarIcon, MessageCircleIcon, PieChartIcon, BarChartIcon, ThumbsUpIcon, ThumbsDownIcon, CalendarIcon, UsersIcon, FacebookIcon, TwitterIcon, LinkIcon, CopyIcon, CheckIcon } from 'lucide-react';
import DropdownMenu, { DropdownItem } from '../components/DropdownMenu';
const DailyVibeCheck = () => {
  const [activeTab, setActiveTab] = useState('trivia');
  const [quoteCategory, setQuoteCategory] = useState('motivational');
  const [savedQuotes, setSavedQuotes] = useState<string[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(24 * 60 * 60); // 24 hours in seconds
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showQuestions, setShowQuestions] = useState(false);
  const [userBadges, setUserBadges] = useState([{
    id: 'pioneer',
    name: 'Pioneer',
    earned: true,
    description: 'Joined a new community'
  }, {
    id: 'social',
    name: 'Social Butterfly',
    earned: false,
    description: 'Attended 5 events'
  }, {
    id: 'connector',
    name: 'Connector',
    earned: true,
    description: 'Active forum participation'
  }, {
    id: 'trivia',
    name: 'Trivia Master',
    earned: false,
    description: 'Scored 100+ points in trivia'
  }]);
  // Daily poll state
  const [dailyPoll, setDailyPoll] = useState({
    question: "What's the best spot for nyama choma in Nairobi?",
    options: [{
      id: 'carnivore',
      text: 'Carnivore',
      votes: 245
    }, {
      id: 'roadhouse',
      text: 'Roadhouse Grill',
      votes: 187
    }, {
      id: 'olepolos',
      text: 'Olepolos Country Club',
      votes: 329
    }, {
      id: 'rangers',
      text: 'Rangers Restaurant',
      votes: 156
    }],
    userVote: null,
    totalVotes: 917
  });
  // State for copied link notification
  const [linkCopied, setLinkCopied] = useState(false);
  const [filteredQuestions, setFilteredQuestions] = useState<any[]>([]);
  const handleVote = (optionId: string) => {
    if (dailyPoll.userVote) return; // Already voted
    setDailyPoll(prev => {
      const newOptions = prev.options.map(option => {
        if (option.id === optionId) {
          return {
            ...option,
            votes: option.votes + 1
          };
        }
        return option;
      });
      return {
        ...prev,
        options: newOptions,
        userVote: optionId,
        totalVotes: prev.totalVotes + 1
      };
    });
  };
  const toggleSaveQuote = (quoteId: string) => {
    if (savedQuotes.includes(quoteId)) {
      setSavedQuotes(savedQuotes.filter(id => id !== quoteId));
    } else {
      setSavedQuotes([...savedQuotes, quoteId]);
    }
  };
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setShowQuestions(true);
    setCurrentQuestion(0);
    setScore(0);
    setQuizCompleted(false);
    // Get unique questions for this session
    const uniqueQuestions = getUniqueQuestions(quizQuestions, category, 10);
    setFilteredQuestions(uniqueQuestions);
  };
  const handleAnswerSubmit = (isCorrect: boolean, points: number, selectedIndex: number) => {
    // Save the user's answer
    const updatedQuestions = [...filteredQuestions];
    updatedQuestions[currentQuestion].userAnswer = selectedIndex;
    updatedQuestions[currentQuestion].isCorrect = isCorrect;
    setFilteredQuestions(updatedQuestions);
    if (isCorrect) {
      setScore(score + points);
    }
    // Show answer for a moment before proceeding
    setTimeout(() => {
      if (currentQuestion < filteredQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setQuizCompleted(true);
        // Check if user earned the Trivia Master badge
        if (score + (isCorrect ? points : 0) >= 100) {
          const updatedBadges = userBadges.map(badge => badge.id === 'trivia' ? {
            ...badge,
            earned: true
          } : badge);
          setUserBadges(updatedBadges);
        }
      }
    }, 1500);
  };
  const resetTrivia = () => {
    setShowQuestions(false);
    setSelectedCategory('');
    setCurrentQuestion(0);
    setScore(0);
    setQuizCompleted(false);
  };
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 0) {
          clearInterval(timer);
          return 24 * 60 * 60;
        }
        return prevTime - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Initialize filtered questions when component mounts
  useEffect(() => {
    // Default to general category on first load
    if (filteredQuestions.length === 0) {
      const initialQuestions = getUniqueQuestions(quizQuestions, 'general', 10);
      setFilteredQuestions(initialQuestions);
      setSelectedCategory('general');
      setShowQuestions(true);
    }
  }, []);
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const quoteCategories = [{
    id: 'motivational',
    name: 'Motivational'
  }, {
    id: 'funny',
    name: 'Funny'
  }, {
    id: 'inspirational',
    name: 'Inspirational'
  }, {
    id: 'love',
    name: 'Love & Relationships'
  }, {
    id: 'success',
    name: 'Success'
  }, {
    id: 'life',
    name: 'Life'
  }, {
    id: 'friendship',
    name: 'Friendship'
  }, {
    id: 'instagram',
    name: 'Instagram Captions'
  }];
  const quotes = [{
    id: '1',
    text: 'The future belongs to those who believe in the beauty of their dreams.',
    author: 'Eleanor Roosevelt',
    category: 'motivational',
    image: '',
    tags: ['dreams', 'future', 'belief']
  }, {
    id: '2',
    text: 'Success is not final, failure is not fatal: It is the courage to continue that counts.',
    author: 'Winston Churchill',
    category: 'motivational',
    image: '',
    tags: ['success', 'failure', 'courage']
  }, {
    id: '3',
    text: "I'm not lazy, I'm just on energy-saving mode.",
    author: 'Unknown',
    category: 'funny',
    image: '',
    tags: ['lazy', 'humor', 'relatable']
  }, {
    id: '4',
    text: "Life is what happens when you're busy making other plans.",
    author: 'John Lennon',
    category: 'life',
    image: '',
    tags: ['life', 'planning', 'wisdom']
  }, {
    id: '5',
    text: "Vibes don't lie. Trust them.",
    author: 'Unknown',
    category: 'instagram',
    image: '',
    tags: ['vibes', 'trust', 'feelings']
  }, {
    id: '6',
    text: 'A true friend is one soul in two bodies.',
    author: 'Aristotle',
    category: 'friendship',
    image: '',
    tags: ['friendship', 'soul', 'connection']
  }];
  const filteredQuotes = quoteCategory === 'all' ? quotes : quotes.filter(quote => quote.category === quoteCategory);
  const quizCategories = [{
    id: 'all',
    name: 'All Categories'
  }, {
    id: 'general',
    name: 'General Knowledge'
  }, {
    id: 'entertainment',
    name: 'Entertainment'
  }, {
    id: 'food',
    name: 'Food & Drink'
  }, {
    id: 'geography',
    name: 'Geography'
  }, {
    id: 'science',
    name: 'Science & Nature'
  }, {
    id: 'history',
    name: 'History'
  }, {
    id: 'sports',
    name: 'Sports'
  }, {
    id: 'culture',
    name: 'Kenyan Culture'
  }];
  const quizQuestions = [{
    question: 'What is the capital city of Kenya?',
    options: ['Mombasa', 'Nairobi', 'Kisumu', 'Nakuru'],
    correctAnswer: 1,
    category: 'geography',
    difficulty: 'easy',
    points: 10
  }, {
    question: 'Which Kenyan athlete won gold in the 1500m at the 2020 Tokyo Olympics?',
    options: ['Eliud Kipchoge', 'Faith Kipyegon', 'David Rudisha', 'Ferdinand Omanyala'],
    correctAnswer: 1,
    category: 'sports',
    difficulty: 'medium',
    points: 20
  }, {
    question: 'Which of these is a traditional Kenyan dish?',
    options: ['Paella', 'Ugali', 'Sushi', 'Pasta'],
    correctAnswer: 1,
    category: 'food',
    difficulty: 'easy',
    points: 10
  }, {
    question: 'What year did Kenya gain independence?',
    options: ['1960', '1963', '1965', '1970'],
    correctAnswer: 1,
    category: 'history',
    difficulty: 'medium',
    points: 20
  }, {
    question: 'Which of these animals is NOT found in Kenya?',
    options: ['Elephant', 'Giraffe', 'Panda', 'Lion'],
    correctAnswer: 2,
    category: 'science',
    difficulty: 'easy',
    points: 10
  }, {
    question: 'Which Kenyan band is known for the song "Sura Yako"?',
    options: ['Sauti Sol', 'H_art the Band', 'Elani', 'Le Band'],
    correctAnswer: 0,
    category: 'entertainment',
    difficulty: 'medium',
    points: 20
  }, {
    question: 'What is the name of the Kenyan currency?',
    options: ['Rand', 'Shilling', 'Naira', 'Dirham'],
    correctAnswer: 1,
    category: 'general',
    difficulty: 'easy',
    points: 10
  }, {
    question: 'Which is the largest national park in Kenya?',
    options: ['Amboseli', 'Nairobi', 'Tsavo', 'Masai Mara'],
    correctAnswer: 2,
    category: 'geography',
    difficulty: 'medium',
    points: 20
  }, {
    question: 'What is the meaning of "Hakuna Matata"?',
    options: ['Hello Friend', 'No Worries', 'Thank You', 'Goodbye'],
    correctAnswer: 1,
    category: 'culture',
    difficulty: 'easy',
    points: 10
  }, {
    question: 'Which lake in Kenya is known for its flamingos?',
    options: ['Lake Victoria', 'Lake Turkana', 'Lake Nakuru', 'Lake Naivasha'],
    correctAnswer: 2,
    category: 'geography',
    difficulty: 'medium',
    points: 20
  }];
  const getUniqueQuestions = (allQuestions: any[], category: string, count: number) => {
    const categoryQuestions = category === 'all' ? allQuestions : allQuestions.filter(q => q.category === category);
    // Shuffle the questions
    const shuffled = [...categoryQuestions].sort(() => 0.5 - Math.random());
    // Return the first 'count' questions or all if less than count
    return shuffled.slice(0, Math.min(count, shuffled.length));
  };
  const currentQuizQuestion = filteredQuestions[currentQuestion];
  const leaderboard = [{
    name: 'John K.',
    points: 560,
    streak: 12
  }, {
    name: 'Mary W.',
    points: 520,
    streak: 8
  }, {
    name: 'David O.',
    points: 490,
    streak: 10
  }, {
    name: 'Sarah N.',
    points: 470,
    streak: 7
  }, {
    name: 'Michael M.',
    points: 460,
    streak: 6
  }, {
    name: 'Elizabeth W.',
    points: 440,
    streak: 5
  }, {
    name: 'Joseph K.',
    points: 420,
    streak: 4
  }, {
    name: 'Jane N.',
    points: 400,
    streak: 3
  }, {
    name: 'Peter O.',
    points: 380,
    streak: 5
  }, {
    name: 'Lucy W.',
    points: 360,
    streak: 2
  }];
  const saveQuoteAsImage = (quoteId: string) => {
    // Create a canvas element and draw the quote on it
    const quoteElement = document.getElementById(`quote-${quoteId}`);
    if (quoteElement) {
      const canvas = document.createElement('canvas');
      canvas.width = quoteElement.offsetWidth * 2;
      canvas.height = quoteElement.offsetHeight * 2;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Set background
        ctx.fillStyle = '#f8f8f8';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Set text styles
        ctx.fillStyle = '#333333';
        ctx.font = '32px Arial';
        ctx.textAlign = 'center';
        // Get the quote text
        const quote = quotes.find(q => q.id === quoteId);
        if (quote) {
          // Draw the quote text
          const text = `"${quote.text}"`;
          const words = text.split(' ');
          let line = '';
          let y = 100;
          // Wrap text
          for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i] + ' ';
            if (ctx.measureText(testLine).width > canvas.width - 60) {
              ctx.fillText(line, canvas.width / 2, y);
              line = words[i] + ' ';
              y += 40;
            } else {
              line = testLine;
            }
          }
          ctx.fillText(line, canvas.width / 2, y);
          // Draw the author
          ctx.font = '24px Arial';
          ctx.fillText(`- ${quote.author}`, canvas.width / 2, y + 60);
          // Convert to image and download
          const dataUrl = canvas.toDataURL('image/jpeg');
          const link = document.createElement('a');
          link.download = `quote-${quoteId}.jpg`;
          link.href = dataUrl;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  };
  const shareQuote = (quote: any) => {
    const quoteText = `"${quote.text}" - ${quote.author}`;
    const shareUrl = `https://jointoday.co.ke/daily-vibe-check`;
    // Open sharing dialog based on platform availability
    if (navigator.share) {
      navigator.share({
        title: 'Daily Quote from JoinToday',
        text: quoteText,
        url: shareUrl
      }).catch(err => {
        console.error('Error sharing:', err);
      });
    } else {
      // Fallback to copying to clipboard
      copyToClipboard(`${quoteText}\n\nFind more daily quotes on JoinToday.co.ke!`);
    }
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };
  return <div className="pb-16" data-id="element-1201">
      <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800" data-id="element-1202">
        <div className="max-w-4xl mx-auto p-4" data-id="element-1203">
          <h1 className="text-2xl font-bold mb-2 flex items-center" data-id="element-1204">
            <CheckSquareIcon className="w-6 h-6 mr-2" data-id="element-1205" />
            Daily Vibe Check
          </h1>
          <div className="flex flex-wrap items-center text-sm text-gray-600 dark:text-gray-300 mb-4" data-id="element-1206">
            <span data-id="element-1207">Get your daily dose of motivation, fun, and knowledge</span>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto p-4" data-id="element-1208">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto" data-id="element-1209">
          <button onClick={() => setActiveTab('poll')} className={`py-2 px-4 whitespace-nowrap ${activeTab === 'poll' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1210">
            Daily Poll
          </button>
          <button onClick={() => {
          setActiveTab('trivia');
          resetTrivia();
        }} className={`py-2 px-4 whitespace-nowrap ${activeTab === 'trivia' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1211">
            Daily Trivia
          </button>
          <button onClick={() => setActiveTab('quotes')} className={`py-2 px-4 whitespace-nowrap ${activeTab === 'quotes' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1212">
            Daily Quotes
          </button>
          <button onClick={() => setActiveTab('leaderboard')} className={`py-2 px-4 whitespace-nowrap ${activeTab === 'leaderboard' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1213">
            Leaderboard
          </button>
          <button onClick={() => setActiveTab('profile')} className={`py-2 px-4 whitespace-nowrap ${activeTab === 'profile' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1214">
            My Profile
          </button>
          <button onClick={() => setActiveTab('rewards')} className={`py-2 px-4 whitespace-nowrap ${activeTab === 'rewards' ? 'border-b-2 border-gray-800 dark:border-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1215">
            JT Points & Rewards
          </button>
        </div>

        {/* Daily Poll Tab */}
        {activeTab === 'poll' && <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6" data-id="element-1216">
            <div className="mb-6" data-id="element-1217">
              <div className="flex justify-between items-center mb-2" data-id="element-1218">
                <h2 className="text-xl font-bold" data-id="element-1219">Daily Poll</h2>
                <div className="flex items-center" data-id="element-1220">
                  <RefreshCwIcon className="w-4 h-4 mr-1" data-id="element-1221" />
                  <span className="text-sm" data-id="element-1222">
                    New poll in: {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1223">
                Share your opinion and see what the community thinks!
              </p>
            </div>
            <div className="mb-6" data-id="element-1224">
              <h3 className="text-lg font-medium mb-4" data-id="element-1225">{dailyPoll.question}</h3>
              <div className="space-y-4" data-id="element-1226">
                {dailyPoll.options.map(option => {
              const percentage = Math.round(option.votes / dailyPoll.totalVotes * 100);
              const isUserVote = dailyPoll.userVote === option.id;
              return <div key={option.id} className="relative" data-id="element-1227">
                      {dailyPoll.userVote ? <div className="flex items-center mb-1" data-id="element-1228">
                          <span className="font-medium" data-id="element-1229">{option.text}</span>
                          {isUserVote && <span className="ml-2 text-xs bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 px-2 py-0.5 rounded-full" data-id="element-1230">
                              Your vote
                            </span>}
                        </div> : <button onClick={() => handleVote(option.id)} className="w-full text-left font-medium mb-1 hover:text-gray-800 dark:hover:text-gray-200" data-id="element-1231">
                          {option.text}
                        </button>}
                      <div className="h-8 w-full bg-gray-200 dark:bg-gray-700 rounded-md overflow-hidden" data-id="element-1232">
                        <div className={`h-full ${isUserVote ? 'bg-gray-800 dark:bg-gray-200' : 'bg-gray-500 dark:bg-gray-500'}`} style={{
                    width: `${percentage}%`
                  }} data-id="element-1233"></div>
                      </div>
                      <div className="flex justify-between text-sm mt-1" data-id="element-1234">
                        <span data-id="element-1235">{option.votes} votes</span>
                        <span data-id="element-1236">{percentage}%</span>
                      </div>
                    </div>;
            })}
              </div>
              <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-300" data-id="element-1237">
                <p data-id="element-1238">Total votes: {dailyPoll.totalVotes}</p>
                {dailyPoll.userVote && <div className="mt-4" data-id="element-1239">
                    <p data-id="element-1240">Thanks for voting! Come back tomorrow for a new poll.</p>
                    <div className="flex justify-center mt-2 space-x-2" data-id="element-1241">
                      <DropdownMenu trigger={<button className="flex items-center text-gray-800 dark:text-gray-200 hover:underline" data-id="element-1243">
                            <ShareIcon className="w-4 h-4 mr-1" data-id="element-1244" />
                            Share Poll
                          </button>} data-id="element-1242">
                        <DropdownItem onClick={() => {
                    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I just voted in today's poll on JoinToday.co.ke: ${dailyPoll.question}`)}&url=${encodeURIComponent('https://jointoday.co.ke/daily-vibe-check')}`, '_blank');
                  }} data-id="element-1245">
                          <div className="flex items-center" data-id="element-1246">
                            <TwitterIcon className="w-4 h-4 mr-2" data-id="element-1247" />
                            <span data-id="element-1248">Share on Twitter</span>
                          </div>
                        </DropdownItem>
                        <DropdownItem onClick={() => {
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://jointoday.co.ke/daily-vibe-check')}&quote=${encodeURIComponent(`I just voted in today's poll on JoinToday.co.ke: ${dailyPoll.question}`)}`, '_blank');
                  }} data-id="element-1249">
                          <div className="flex items-center" data-id="element-1250">
                            <FacebookIcon className="w-4 h-4 mr-2" data-id="element-1251" />
                            <span data-id="element-1252">Share on Facebook</span>
                          </div>
                        </DropdownItem>
                        <DropdownItem onClick={() => {
                    window.open(`https://wa.me/?text=${encodeURIComponent(`I just voted in today's poll on JoinToday.co.ke: ${dailyPoll.question} Check it out: https://jointoday.co.ke/daily-vibe-check`)}`, '_blank');
                  }} data-id="element-1253">
                          <div className="flex items-center" data-id="element-1254">
                            <span className="w-4 h-4 mr-2 text-center" data-id="element-1255">📱</span>
                            <span data-id="element-1256">Share on WhatsApp</span>
                          </div>
                        </DropdownItem>
                        <DropdownItem onClick={() => {
                    copyToClipboard(`Check out today's poll on JoinToday.co.ke: ${dailyPoll.question} https://jointoday.co.ke/daily-vibe-check`);
                  }} data-id="element-1257">
                          <div className="flex items-center" data-id="element-1258">
                            <LinkIcon className="w-4 h-4 mr-2" data-id="element-1259" />
                            <span data-id="element-1260">Copy Link</span>
                          </div>
                        </DropdownItem>
                      </DropdownMenu>
                    </div>
                  </div>}
              </div>
            </div>
          </div>}

        {/* Trivia Tab */}
        {activeTab === 'trivia' && <div data-id="element-1261">
            <div className="mb-6" data-id="element-1262">
              <div className="flex justify-between items-center" data-id="element-1263">
                <h2 className="text-lg font-bold" data-id="element-1264">Daily Trivia Challenge</h2>
                <div className="flex items-center" data-id="element-1265">
                  <RefreshCwIcon className="w-4 h-4 mr-1" data-id="element-1266" />
                  <span className="text-sm" data-id="element-1267">
                    Resets in: {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1" data-id="element-1268">
                Test your knowledge with our daily trivia questions. The harder
                the question, the more points you earn!
              </p>
            </div>
            {!showQuestions ? <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6" data-id="element-1269">
                <h3 className="font-bold text-lg mb-4" data-id="element-1270">Select a Category</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-1271">
                  {quizCategories.map(category => <button key={category.id} onClick={() => handleCategorySelect(category.id)} className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-1272">
                      {category.name}
                    </button>)}
                </div>
              </div> : !quizCompleted ? <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6" data-id="element-1273">
                <div className="mb-4" data-id="element-1274">
                  <div className="flex justify-between items-center mb-2" data-id="element-1275">
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1276">
                      Question {currentQuestion + 1} of{' '}
                      {filteredQuestions.length}
                    </span>
                    <span className="text-sm bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-2 py-0.5" data-id="element-1277">
                      {filteredQuestions[currentQuestion]?.category} •{' '}
                      {filteredQuestions[currentQuestion]?.difficulty} •{' '}
                      {filteredQuestions[currentQuestion]?.points} points
                    </span>
                  </div>
                  <h3 className="text-lg font-medium" data-id="element-1278">
                    {filteredQuestions[currentQuestion]?.question}
                  </h3>
                </div>
                <div className="space-y-3" data-id="element-1279">
                  {filteredQuestions[currentQuestion]?.options.map((option: string, index: number) => {
              const question = filteredQuestions[currentQuestion];
              const correctAnswer = question?.correctAnswer;
              const userAnswer = question?.userAnswer;
              let className = 'w-full text-left p-3 border border-gray-300 dark:border-gray-600 rounded-md';
              // If user has answered
              if (userAnswer !== undefined) {
                if (index === correctAnswer) {
                  className += ' bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-800';
                } else if (index === userAnswer && userAnswer !== correctAnswer) {
                  className += ' bg-gray-400 text-white dark:bg-gray-600';
                }
              } else {
                className += ' hover:bg-gray-50 dark:hover:bg-gray-700';
              }
              return <button key={index} onClick={() => userAnswer === undefined && handleAnswerSubmit(index === correctAnswer, question.points, index)} className={className} disabled={userAnswer !== undefined} data-id="element-1280">
                          {option}
                          {userAnswer !== undefined && index === correctAnswer && <span className="ml-2" data-id="element-1281">✓</span>}
                          {userAnswer !== undefined && index === userAnswer && index !== correctAnswer && <span className="ml-2" data-id="element-1282">✗</span>}
                        </button>;
            })}
                </div>
                <div className="mt-4 text-center" data-id="element-1283">
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1284">
                    Current score: {score} points
                  </p>
                </div>
              </div> : <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center" data-id="element-1285">
                <div className="mb-4" data-id="element-1286">
                  <AwardIcon className="w-12 h-12 mx-auto text-gray-800 dark:text-gray-200 mb-2" data-id="element-1287" />
                  <h3 className="text-xl font-bold" data-id="element-1288">Quiz Completed!</h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1289">
                    You scored {score} points today
                  </p>
                </div>
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-1290">
                  <p className="text-sm" data-id="element-1291">
                    Come back tomorrow for a new set of questions and a chance
                    to earn more JT Points!
                  </p>
                </div>
                <div className="flex space-x-4 justify-center" data-id="element-1292">
                  <button onClick={resetTrivia} className="bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md px-4 py-2" data-id="element-1293">
                    Try Another Category
                  </button>
                  <button onClick={() => setActiveTab('leaderboard')} className="bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md px-4 py-2" data-id="element-1294">
                    View Leaderboard
                  </button>
                </div>
              </div>}
          </div>}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && <div data-id="element-1295">
            <div className="mb-6 overflow-x-auto" data-id="element-1296">
              <div className="flex space-x-2 pb-2" data-id="element-1297">
                <button onClick={() => setQuoteCategory('all')} className={`px-3 py-1 border rounded-full whitespace-nowrap ${quoteCategory === 'all' ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-1298">
                  All Categories
                </button>
                {quoteCategories.map(category => <button key={category.id} onClick={() => setQuoteCategory(category.id)} className={`px-3 py-1 border rounded-full whitespace-nowrap ${quoteCategory === category.id ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-1299">
                    {category.name}
                  </button>)}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1300">
              {filteredQuotes.map(quote => <div key={quote.id} className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden" data-id="element-1301">
                  <div className="h-48 bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600 flex items-center justify-center" data-id="element-1302">
                    <div className="p-6 text-center" id={`quote-${quote.id}`} data-id="element-1303">
                      <p className="text-lg font-medium italic" data-id="element-1304">
                        "{quote.text}"
                      </p>
                      <p className="mt-2" data-id="element-1305">- {quote.author}</p>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-1306">
                    <div className="flex flex-wrap gap-2 mb-3" data-id="element-1307">
                      {quote.tags.map((tag, i) => <span key={i} className="text-xs bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-2 py-0.5" data-id="element-1308">
                          #{tag}
                        </span>)}
                    </div>
                    <div className="flex justify-between items-center" data-id="element-1309">
                      <div className="flex space-x-2" data-id="element-1310">
                        <button onClick={() => saveQuoteAsImage(quote.id)} className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm flex items-center" data-id="element-1311">
                          <DownloadIcon className="w-4 h-4 mr-1" data-id="element-1312" />
                          Save as Image
                        </button>
                        <DropdownMenu trigger={<button className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm flex items-center" data-id="element-1314">
                              <ShareIcon className="w-4 h-4 mr-1" data-id="element-1315" />
                              Share
                            </button>} data-id="element-1313">
                          <DropdownItem onClick={() => {
                      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`"${quote.text}" - ${quote.author}`)}&url=${encodeURIComponent('https://jointoday.co.ke/daily-vibe-check')}`, '_blank');
                    }} data-id="element-1316">
                            <div className="flex items-center" data-id="element-1317">
                              <TwitterIcon className="w-4 h-4 mr-2" data-id="element-1318" />
                              <span data-id="element-1319">Share on Twitter</span>
                            </div>
                          </DropdownItem>
                          <DropdownItem onClick={() => {
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://jointoday.co.ke/daily-vibe-check')}&quote=${encodeURIComponent(`"${quote.text}" - ${quote.author}`)}`, '_blank');
                    }} data-id="element-1320">
                            <div className="flex items-center" data-id="element-1321">
                              <FacebookIcon className="w-4 h-4 mr-2" data-id="element-1322" />
                              <span data-id="element-1323">Share on Facebook</span>
                            </div>
                          </DropdownItem>
                          <DropdownItem onClick={() => {
                      window.open(`https://wa.me/?text=${encodeURIComponent(`"${quote.text}" - ${quote.author}\n\nFind more daily quotes on JoinToday.co.ke!`)}`, '_blank');
                    }} data-id="element-1324">
                            <div className="flex items-center" data-id="element-1325">
                              <span className="w-4 h-4 mr-2 text-center" data-id="element-1326">
                                📱
                              </span>
                              <span data-id="element-1327">Share on WhatsApp</span>
                            </div>
                          </DropdownItem>
                          <DropdownItem onClick={() => {
                      copyToClipboard(`"${quote.text}" - ${quote.author}`);
                    }} data-id="element-1328">
                            <div className="flex items-center" data-id="element-1329">
                              <CopyIcon className="w-4 h-4 mr-2" data-id="element-1330" />
                              <span data-id="element-1331">Copy Quote</span>
                            </div>
                          </DropdownItem>
                        </DropdownMenu>
                      </div>
                      <button onClick={() => toggleSaveQuote(quote.id)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full" data-id="element-1332">
                        <BookmarkIcon className={`w-5 h-5 ${savedQuotes.includes(quote.id) ? 'fill-gray-800 dark:fill-gray-200 text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-1333" />
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
            {linkCopied && <div className="fixed bottom-4 right-4 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 px-4 py-2 rounded-md flex items-center" data-id="element-1334">
                <CheckIcon className="w-4 h-4 mr-2" data-id="element-1335" />
                <span data-id="element-1336">Copied to clipboard</span>
              </div>}
          </div>}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && <div data-id="element-1337">
            <div className="mb-6" data-id="element-1338">
              <h2 className="text-lg font-bold mb-2" data-id="element-1339">Top Trivia Players</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1340">
                The top 10 players with the highest points. Top performers for
                10 consecutive days earn special JT Points rewards!
              </p>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden" data-id="element-1341">
              <div className="bg-gray-100 dark:bg-gray-700 p-3 border-b border-gray-300 dark:border-gray-600 grid grid-cols-12 gap-2" data-id="element-1342">
                <div className="col-span-1 font-medium text-sm" data-id="element-1343">#</div>
                <div className="col-span-5 font-medium text-sm" data-id="element-1344">Player</div>
                <div className="col-span-3 font-medium text-sm text-center" data-id="element-1345">
                  Points
                </div>
                <div className="col-span-3 font-medium text-sm text-center" data-id="element-1346">
                  Day Streak
                </div>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1347">
                {leaderboard.map((player, index) => <div key={index} className="p-3 grid grid-cols-12 gap-2 items-center" data-id="element-1348">
                    <div className="col-span-1 text-sm" data-id="element-1349">{index + 1}</div>
                    <div className="col-span-5 text-sm" data-id="element-1350">{player.name}</div>
                    <div className="col-span-3 text-sm text-center" data-id="element-1351">
                      {player.points}
                    </div>
                    <div className="col-span-3 text-sm text-center flex items-center justify-center" data-id="element-1352">
                      {player.streak} days{' '}
                      <TrophyIcon className="w-4 h-4 ml-1 text-gray-800 dark:text-gray-200" data-id="element-1353" />
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="mt-6 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700" data-id="element-1354">
              <h3 className="font-medium mb-2" data-id="element-1355">Your Stats</h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-1356">
                <div className="text-center" data-id="element-1357">
                  <p className="text-2xl font-bold" data-id="element-1358">{score}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300" data-id="element-1359">
                    Today's Points
                  </p>
                </div>
                <div className="text-center" data-id="element-1360">
                  <p className="text-2xl font-bold" data-id="element-1361">0</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300" data-id="element-1362">
                    Day Streak
                  </p>
                </div>
                <div className="text-center" data-id="element-1363">
                  <p className="text-2xl font-bold" data-id="element-1364">--</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300" data-id="element-1365">
                    Ranking
                  </p>
                </div>
              </div>
            </div>
          </div>}

        {/* User Profile Tab */}
        {activeTab === 'profile' && <div data-id="element-1366">
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden mb-6" data-id="element-1367">
              <div className="bg-gradient-to-r from-green-700 to-red-700 p-6 text-white" data-id="element-1368">
                <div className="flex items-center" data-id="element-1369">
                  <div className="w-20 h-20 bg-white dark:bg-gray-200 rounded-full flex items-center justify-center mr-4 border-4 border-white dark:border-gray-200" data-id="element-1370">
                    <span className="text-3xl font-bold text-gray-800 dark:text-gray-800" data-id="element-1371">
                      JD
                    </span>
                  </div>
                  <div data-id="element-1372">
                    <h2 className="text-xl font-bold" data-id="element-1373">John Doe</h2>
                    <p className="text-sm opacity-90" data-id="element-1374">
                      Member since January 2025
                    </p>
                    <div className="flex items-center mt-1" data-id="element-1375">
                      <StarIcon className="w-4 h-4 mr-1" data-id="element-1376" />
                      <span className="text-sm" data-id="element-1377">Level 3 Member</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4" data-id="element-1378">
                <h3 className="font-bold text-lg mb-3" data-id="element-1379">My Badges</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-1380">
                  {userBadges.map(badge => <div key={badge.id} className={`border ${badge.earned ? 'border-gray-800 dark:border-gray-200' : 'border-gray-300 dark:border-gray-600'} rounded-lg p-3 text-center`} data-id="element-1381">
                      <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${badge.earned ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800' : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'}`} data-id="element-1382">
                        <BadgeIcon className="w-6 h-6" data-id="element-1383" />
                      </div>
                      <h4 className="font-medium text-sm" data-id="element-1384">{badge.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1385">
                        {badge.description}
                      </p>
                      {!badge.earned && <span className="text-xs text-gray-400 dark:text-gray-500 block mt-2" data-id="element-1386">
                          Not yet earned
                        </span>}
                    </div>)}
                </div>
                <h3 className="font-bold text-lg mb-3" data-id="element-1387">Activity Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-1388">
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1389">
                    <h4 className="font-medium mb-2 flex items-center" data-id="element-1390">
                      <BarChartIcon className="w-4 h-4 mr-2" data-id="element-1391" />
                      Participation Stats
                    </h4>
                    <div className="space-y-2" data-id="element-1392">
                      <div className="flex justify-between items-center" data-id="element-1393">
                        <span className="text-sm" data-id="element-1394">Communities Joined</span>
                        <span className="font-medium" data-id="element-1395">3</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1396">
                        <span className="text-sm" data-id="element-1397">Events Attended</span>
                        <span className="font-medium" data-id="element-1398">2</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1399">
                        <span className="text-sm" data-id="element-1400">Forum Posts</span>
                        <span className="font-medium" data-id="element-1401">12</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-1402">
                        <span className="text-sm" data-id="element-1403">Trivia Points</span>
                        <span className="font-medium" data-id="element-1404">180</span>
                      </div>
                    </div>
                  </div>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1405">
                    <h4 className="font-medium mb-2 flex items-center" data-id="element-1406">
                      <PieChartIcon className="w-4 h-4 mr-2" data-id="element-1407" />
                      Interests
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-1408">
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs" data-id="element-1409">
                        Running
                      </span>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs" data-id="element-1410">
                        Technology
                      </span>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs" data-id="element-1411">
                        Photography
                      </span>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs" data-id="element-1412">
                        Music
                      </span>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs" data-id="element-1413">
                        Food
                      </span>
                      <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs" data-id="element-1414">
                        Travel
                      </span>
                    </div>
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-3" data-id="element-1415">Recent Activity</h3>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1416">
                  <div className="p-3 flex items-start" data-id="element-1417">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2 mr-3" data-id="element-1418">
                      <MessageCircleIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1419" />
                    </div>
                    <div data-id="element-1420">
                      <p className="text-sm" data-id="element-1421">
                        Posted in{' '}
                        <span className="font-medium" data-id="element-1422">
                          Tech Enthusiasts Forum
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1423">
                        2 days ago
                      </p>
                    </div>
                  </div>
                  <div className="p-3 flex items-start" data-id="element-1424">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2 mr-3" data-id="element-1425">
                      <CalendarIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1426" />
                    </div>
                    <div data-id="element-1427">
                      <p className="text-sm" data-id="element-1428">
                        Attended{' '}
                        <span className="font-medium" data-id="element-1429">Nairobi Tech Meetup</span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1430">
                        1 week ago
                      </p>
                    </div>
                  </div>
                  <div className="p-3 flex items-start" data-id="element-1431">
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-2 mr-3" data-id="element-1432">
                      <UsersIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-1433" />
                    </div>
                    <div data-id="element-1434">
                      <p className="text-sm" data-id="element-1435">
                        Joined{' '}
                        <span className="font-medium" data-id="element-1436">WeRun Nairobi</span>{' '}
                        community
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1437">
                        2 weeks ago
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Rewards Tab */}
        {activeTab === 'rewards' && <div data-id="element-1438">
            <div className="mb-6" data-id="element-1439">
              <h2 className="text-lg font-bold mb-2" data-id="element-1440">JT Points & Rewards</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1441">
                Earn JT Points through daily activities and redeem them for
                exclusive discounts and special offers.
              </p>
            </div>
            <div className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 mb-6" data-id="element-1442">
              <div className="flex justify-between items-center" data-id="element-1443">
                <div data-id="element-1444">
                  <h3 className="font-medium" data-id="element-1445">Your JT Points</h3>
                  <p className="text-3xl font-bold" data-id="element-1446">180</p>
                </div>
                <button className="bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 rounded-md px-4 py-2 text-sm" data-id="element-1447">
                  Redeem Points
                </button>
              </div>
            </div>
            <div className="mb-6" data-id="element-1448">
              <h3 className="font-medium mb-3" data-id="element-1449">How to Earn Points</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1450">
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1451">
                  <div className="flex items-center mb-2" data-id="element-1452">
                    <BrainIcon className="w-5 h-5 mr-2 text-gray-800 dark:text-gray-200" data-id="element-1453" />
                    <h4 className="font-medium" data-id="element-1454">Daily Trivia</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1455">
                    Complete daily trivia challenges to earn points based on
                    difficulty.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-1456">
                    Easy: 10 points | Medium: 20 points | Hard: 30 points
                  </p>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1457">
                  <div className="flex items-center mb-2" data-id="element-1458">
                    <HeartIcon className="w-5 h-5 mr-2 text-gray-800 dark:text-gray-200" data-id="element-1459" />
                    <h4 className="font-medium" data-id="element-1460">Daily Check-in</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1461">
                    Visit JoinToday daily to earn streak bonuses.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-1462">
                    5 points per day | Bonus for consecutive days
                  </p>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1463">
                  <div className="flex items-center mb-2" data-id="element-1464">
                    <ShareIcon className="w-5 h-5 mr-2 text-gray-800 dark:text-gray-200" data-id="element-1465" />
                    <h4 className="font-medium" data-id="element-1466">Share Content</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1467">
                    Share quotes, deals, and events with friends.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-1468">
                    2 points per share | Max 10 points per day
                  </p>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1469">
                  <div className="flex items-center mb-2" data-id="element-1470">
                    <TrophyIcon className="w-5 h-5 mr-2 text-gray-800 dark:text-gray-200" data-id="element-1471" />
                    <h4 className="font-medium" data-id="element-1472">Leaderboard Ranking</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1473">
                    Earn bonus points by ranking in the top 10 for 10
                    consecutive days.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2" data-id="element-1474">
                    500 bonus points | Special badge on your profile
                  </p>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1475">
              <h3 className="font-medium mb-3" data-id="element-1476">Redeem Your Points</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1477">
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1478">
                  <h4 className="font-medium mb-2" data-id="element-1479">Ticket Discounts</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1480">
                    Use your points for discounts on event tickets.
                  </p>
                  <div className="flex justify-between items-center" data-id="element-1481">
                    <span className="text-sm" data-id="element-1482">200 points</span>
                    <button className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1" data-id="element-1483">
                      Redeem
                    </button>
                  </div>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-1484">
                  <h4 className="font-medium mb-2" data-id="element-1485">Exclusive Deals</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3" data-id="element-1486">
                    Access special offers from our merchant partners.
                  </p>
                  <div className="flex justify-between items-center" data-id="element-1487">
                    <span className="text-sm" data-id="element-1488">300 points</span>
                    <button className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1" disabled data-id="element-1489">
                      Redeem
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700" data-id="element-1490">
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1491">
                <strong data-id="element-1492">Note:</strong> JT Points are not a form of gambling or
                game of chance. Points are earned through participation and
                knowledge, and only top daily performers are rewarded.
              </p>
            </div>
          </div>}
      </div>
    </div>;
};
export default DailyVibeCheck;